import type { ComponentProps } from "react";

import { cx } from "./cx";

/**
 * The type roles, and which side of the line each one lives on.
 *
 * Headings, `.hint`, `hr`, `em` and `code` are styled by `app.css`'s base layer rather than
 * by utilities here, and that is deliberate: `/privacy/`, `/terms/` and `/home/` are static
 * pages that link that stylesheet and never load this bundle, and they use every one of
 * them. Two definitions of the heading scale would be two things to keep in agreement.
 * So these components render the semantic element and let the shared base style it.
 *
 * `Lede` and `Muted` are the other side of that line -- nothing outside the app uses them
 * -- so they carry utilities.
 */

/**
 * `h2`, and never `h1`. The header carries the only `h1` on every screen but the sign-in
 * front door, which hides that header and puts its own `h1` in the hero instead -- so a
 * screen adding one here would give the page two.
 */
export function ScreenHeading(props: ComponentProps<"h2">) {
  return <h2 {...props} />;
}

/** Panel headings: mono caps, the instrument-label register. Styled as `h3` in the base. */
export function SubHeading(props: ComponentProps<"h3">) {
  return <h3 {...props} />;
}

/** The sentence under a heading that says what the screen is for. */
export function Lede({ className, ...rest }: ComponentProps<"p">) {
  return <p className={cx("mt-0 mx-0 mb-4 text-lede text-ink-soft", className)} {...rest} />;
}

/** Standing help text: smaller, quieter, and never the thing being asked for. */
export function Hint({ className, ...rest }: ComponentProps<"p">) {
  return <p className={cx("hint", className)} {...rest} />;
}

export function Muted({ className, ...rest }: ComponentProps<"span">) {
  return <span className={cx("text-note text-ink-soft", className)} {...rest} />;
}
